import { Typography } from '@/components/atoms'
import { WithInfoText } from '@/components/hoc/WithInfoText'
import { Breakpoint, ThemeColor, Categories } from '@/enums'
import { calcYearlyPrice } from '@/pages/LocationPage/helpers'
import { theme } from '@/theme'
import {
  formatMoney,
  themeBreakpointDown,
  themeColor,
  themeTransition,
  useIsMobile,
} from '@/utils'
import { Button } from '@surein/ui'
import { ArrowRight, ChevronDown, Smile } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ifProp } from 'styled-tools'
import { ShowWhen } from '../../hoc/ShowWhen'
import { Column, Divider, Row } from '../../styled'
import {
  CartProduct,
  MobileCartProduct,
  MultipleProductsCard,
} from './CartProduct'
import { Container, MobileContainer } from './styles'
import { usePriceCalculations } from './usePriceCalculations'

type PaymentPeriod = 'monthly' | 'yearly'

interface ProductData {
  grossPrice: number
  originalGrossPrice?: number
  paymentPeriod: PaymentPeriod
  insuranceProductId: string
  carrierData: {
    logoUrl: string
    name: string
  }
  categoryId: keyof typeof Categories
}

interface CheckoutPriceCardBaseProps {
  data: ProductData[]
  buttonText?: string
  onButtonClick?: () => void
  hideProducts?: boolean
}

interface MobileCheckoutPriceCardBaseProps {
  data: ProductData[]
  isCheckoutDetailsOpen: boolean
  toggle: () => void
}

interface YouWillBeChargedSectionProps {
  data: ProductData[]
  isMobile?: boolean
}

interface OrderDetailsTriggerProps {
  isOpen: boolean
}

interface ProductsDropdownProps {
  isOpen: boolean
}

interface BadgeContainerProps {
  dark?: boolean
}

const Price = styled(Typography)`
  text-align: end;

  span {
    margin-left: 4px;
    margin-right: 8px;
  }

  span:last-of-type {
    margin-right: 0;
  }
`

const TotalPriceRow = styled(Row)`
  gap: 8px;
  align-items: center;
  justify-content: space-between;

  ${Price}:first-child {
    ${themeBreakpointDown(Breakpoint.lg)} {
      font-weight: 500;
      color: ${themeColor(ThemeColor.b50)};
      font-size: 14px;
    }
  }

  ${themeBreakpointDown(Breakpoint.lg)} {
    flex-wrap: wrap;
  }
`

const OrderDetailsTrigger = styled(Typography)<OrderDetailsTriggerProps>`
  display: flex;
  cursor: pointer;
  height: fit-content;
  align-items: center;
  gap: 2px;

  svg {
    transform: ${ifProp('isOpen', 'rotate(180deg)', 'rotate(0deg)')};
  }
`

const ProductsDropdown = styled(Column)<ProductsDropdownProps>`
  gap: 4px;
  max-height: ${ifProp('isOpen', '50vh', '0')};
  opacity: ${ifProp('isOpen', '1', '0')};
  transform: ${ifProp('isOpen', 'translateY(0)', 'translateY(-10px)')};
  transition: ${themeTransition()};
  padding-bottom: 4px;

  ::-webkit-scrollbar {
    display: none;
  }

  ${Divider} {
    margin: 20px 0;
  }
`

const BadgeContainer = styled(Row)<BadgeContainerProps>`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 100px;
  background: ${(props) =>
    props.dark ? props.theme.color.glass100 : props.theme.color.b0};
  width: fit-content;
`

const useMessages = () => {
  const { t } = useTranslation()

  return {
    price: t('recommendations.details.grossPrice'),
    total: t('checkout.total'),
    seeDetails: t('checkout.seeDetails'),
    hideDetails: t('checkout.hideDetails'),
    includesIpt: t('checkout.includesIpt'),
    subtotal: t('checkout_subtotal'),
    discount: t('surein_discount'),
    yearly: t('recommendationPricePeriods.yearly'),
    sepa: t('sepa'),
    youWillBeCharged: t('checkout.youWillBeCharged'),
    recommendationRate: t('checkout.recommendationRate'),
    totalInfoText: t('checkout.totalInfoText'),
  }
}

const calculateTotalPriceAndPaymentPeriod = (data: ProductData[]) => {
  let totalPrice: number
  let paymentPeriod: PaymentPeriod

  const allItemsHaveSamePaymentPeriod = data.every(
    (item, _, arr) => item.paymentPeriod === arr[0].paymentPeriod,
  )

  // If there is no data, use these defaults
  if (!data.length) {
    totalPrice = 0
    paymentPeriod = 'monthly'
  } else if (allItemsHaveSamePaymentPeriod) {
    // If all items have the same payment period, use that period
    totalPrice = data.reduce((acc, item) => acc + item.grossPrice, 0)
    paymentPeriod = data[0].paymentPeriod
  } else {
    // If there is no common payment period, visually convert all to yearly
    totalPrice = calcYearlyPrice(data)
    paymentPeriod = 'yearly'
  }

  return { totalPrice, paymentPeriod }
}

const YouWillBeChargedSection: React.FC<YouWillBeChargedSectionProps> = ({
  data,
  isMobile,
}) => {
  const { t } = useTranslation()

  const { totalPrice, paymentPeriod } = calculateTotalPriceAndPaymentPeriod(
    data,
  )

  return (
    <TotalPriceRow>
      <WithInfoText
        variant="primary"
        content={t('checkout.youWillBeChargedInfoText')}
      >
        <Price variant="footnote" color={ThemeColor.b50}>
          {t('checkout.youWillBeCharged')}
        </Price>
      </WithInfoText>
      <Price bold color={ThemeColor.b100} variant="footnote">
        {formatMoney(totalPrice)}
        <Typography
          color={ThemeColor.b50}
          variant={isMobile ? 'micro' : 'badgeText'}
        >
          / {t(`paymentPeriodNouns.${paymentPeriod}`)}
        </Typography>
      </Price>
    </TotalPriceRow>
  )
}

export const CheckoutPriceCardBase: React.FC<CheckoutPriceCardBaseProps> = ({
  data,
  buttonText,
  onButtonClick,
  hideProducts,
}) => {
  const messages = useMessages()
  const { isMobile } = useIsMobile(Breakpoint.sm)
  const {
    displayPrice,
    displayGrossPrice,
    displayDiscount,
    displayPeriod,
  } = usePriceCalculations(data)

  return (
    <Container>
      <BadgeContainer>
        <Smile size={16} color={theme.color.success1} />
        <Typography color={ThemeColor.success1} variant="micro">
          {messages.recommendationRate}
        </Typography>
      </BadgeContainer>
      <ShowWhen when={!isMobile && !hideProducts}>
        {data.map((productData) => (
          <CartProduct {...productData} key={productData.insuranceProductId} />
        ))}
      </ShowWhen>
      <Column gap="8px">
        <Row gap="8px" justifyContent="space-between">
          <Typography color={ThemeColor.b50} variant="p2Body">
            {messages.subtotal}
          </Typography>
          <Price bold variant="badgeText">
            {formatMoney(displayGrossPrice)}
            <Typography color={ThemeColor.b50} variant="badgeText">
              / {messages[displayPeriod]}
            </Typography>
          </Price>
        </Row>
        {!!displayDiscount && (
          <Row gap="8px" justifyContent="space-between">
            <Typography color={ThemeColor.b50} variant="p2Body">
              {messages.discount}
            </Typography>
            <Price bold color={ThemeColor.success2} variant="badgeText">
              -{formatMoney(displayDiscount)}
              <Typography color={ThemeColor.b50} variant="badgeText">
                / {messages[displayPeriod]}
              </Typography>
            </Price>
          </Row>
        )}
        <Divider color={ThemeColor.b30} />
        <TotalPriceRow>
          <WithInfoText variant="primary" content={messages.totalInfoText}>
            <Price bold variant="p1Body">
              {messages.total}
              <Typography color={ThemeColor.b50} variant="p2Body">
                {messages.includesIpt}
              </Typography>
            </Price>
          </WithInfoText>
          <Price bold variant="h5">
            {formatMoney(displayPrice)}
            <Typography color={ThemeColor.b50} variant="badgeText">
              / {messages[displayPeriod]}
            </Typography>
          </Price>
        </TotalPriceRow>
        <YouWillBeChargedSection data={data} />
      </Column>
      {onButtonClick && (
        <Button
          suffixIcon={ArrowRight}
          onClick={onButtonClick}
          variant="primary"
          shape="square"
        >
          {buttonText}
        </Button>
      )}
    </Container>
  )
}

export const MobileCheckoutPriceCardBase: React.FC<MobileCheckoutPriceCardBaseProps> = ({
  data,
  isCheckoutDetailsOpen,
  toggle,
}) => {
  const messages = useMessages()
  const {
    displayPrice,
    displayGrossPrice,
    displayDiscount,
    displayPeriod,
  } = usePriceCalculations(data)

  return (
    <MobileContainer>
      <Column gap="8px">
        <Column gap="4px">
          {data.length > 1 ? (
            <MultipleProductsCard
              productCount={data.length}
              displayPrice={displayPrice}
              displayPeriod={displayPeriod}
            />
          ) : (
            data.map((productData) => (
              <MobileCartProduct
                {...productData}
                key={productData.insuranceProductId}
              />
            ))
          )}
        </Column>
        <Column gap="8px">
          <Row gap="8px" justifyContent="space-between">
            <OrderDetailsTrigger
              color={ThemeColor.info1}
              isOpen={isCheckoutDetailsOpen}
              onClick={toggle}
              variant="badgeText"
            >
              {isCheckoutDetailsOpen
                ? messages.hideDetails
                : messages.seeDetails}
              <ChevronDown size={12} />
            </OrderDetailsTrigger>
            <BadgeContainer dark>
              <Smile size={16} color={theme.color.success1} />
              <Typography color={ThemeColor.success1} variant="micro">
                {messages.recommendationRate}
              </Typography>
            </BadgeContainer>
          </Row>
          <ProductsDropdown isOpen={isCheckoutDetailsOpen}>
            <Column gap="8px">
              <TotalPriceRow>
                <Typography color={ThemeColor.b50} variant="micro">
                  {messages.subtotal}
                </Typography>
                <Price bold color={ThemeColor.b0} variant="badgeText">
                  {formatMoney(displayGrossPrice)}
                  <Typography color={ThemeColor.b50} variant="micro">
                    / {messages[displayPeriod]}
                  </Typography>
                </Price>
              </TotalPriceRow>
              {!!displayDiscount && (
                <TotalPriceRow>
                  <>
                    <Typography color={ThemeColor.b50} variant="micro">
                      {messages.discount}
                    </Typography>
                    <Price bold color={ThemeColor.success2} variant="micro">
                      -{formatMoney(displayDiscount)}
                      <Typography color={ThemeColor.b50} variant="micro">
                        / {messages[displayPeriod]}
                      </Typography>
                    </Price>
                  </>
                </TotalPriceRow>
              )}
              <TotalPriceRow>
                <WithInfoText
                  variant="primary"
                  content={messages.totalInfoText}
                >
                  <Price bold variant="micro">
                    {messages.total}
                    <Typography color={ThemeColor.b50} variant="p2Body">
                      {messages.includesIpt}
                    </Typography>
                  </Price>
                </WithInfoText>
                <Price bold color={ThemeColor.b100} variant="micro">
                  {formatMoney(displayPrice)}
                  <Typography color={ThemeColor.b50} variant="micro">
                    / {messages[displayPeriod]}
                  </Typography>
                </Price>
              </TotalPriceRow>
              <YouWillBeChargedSection data={data} isMobile={true} />
            </Column>
          </ProductsDropdown>
        </Column>
      </Column>
    </MobileContainer>
  )
}
